<script setup>
import { ref, computed, onMounted, h } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';

import Table from 'dashboard/components/table/Table.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';

import WppConnectAPI from 'dashboard/routes/dashboard/settings/inbox/channels/WppConnectAPI';
import CommonWhatsappAPI from 'dashboard/routes/dashboard/settings/inbox/channels/commonWhatsapp/api';

const props = defineProps({
  show: Boolean,
  isCodeChat: Boolean,
  availableContacts: { type: Array, default: () => [] },
  currentMembers: { type: Array, default: () => [] },
  inbox: { type: Object, default: () => {} },
  currentContact: { type: Object, default: () => {} },
});

const emit = defineEmits(['onClose', 'addedNewContacts']);
const { t } = useI18n();
const route = useRoute();

const contacts = ref([]);
const selectedNewContacts = ref([]);
const currentAPI = ref(props.isCodeChat ? CommonWhatsappAPI : WppConnectAPI);

const tableData = computed(() =>
  contacts.value.map(item => ({
    ...item,
    phone_number: item.phone_number || '---',
  }))
);

const handleSelection = (event, phone) => {
  if (event.target.checked) {
    if (!selectedNewContacts.value.includes(phone)) {
      selectedNewContacts.value.push(phone);
    }
  } else {
    selectedNewContacts.value = selectedNewContacts.value.filter(
      p => p !== phone
    );
  }
};

const columnHelper = createColumnHelper();

const columns = [
  columnHelper.accessor('name', {
    header: t('CONTACTS_PAGE.LIST.TABLE_HEADER.NAME'),
    cell: cellProps => {
      const row = cellProps.row.original;
      return h('div', { class: 'row--user-block' }, [
        h(Thumbnail, {
          src: row.thumbnail,
          size: '32px',
          username: row.name,
          status: row.availability_status,
        }),
        h('div', { class: 'user-block' }, [
          h(
            'router-link',
            {
              to: `/app/accounts/${route.params.accountId}/contacts/${row.id}`,
              class: 'user-name',
            },
            [
              h('h6', { class: 'sub-block-title text-truncate' }, row.name),
              h(
                'button',
                {
                  class: 'button clear small link view-details--button',
                },
                t('CONTACTS_PAGE.LIST.VIEW_DETAILS')
              ),
            ]
          ),
        ]),
      ]);
    },
  }),
  columnHelper.accessor('phone_number', {
    header: t('CONTACTS_PAGE.LIST.TABLE_HEADER.PHONE_NUMBER'),
    cell: cellProps => cellProps.getValue(),
  }),
  columnHelper.accessor('add_phone', {
    header: 'Selecionar',
    cell: cellProps => {
      const phone = cellProps.row.original.phone_number;
      return h('input', {
        type: 'checkbox',
        class: 'checkbox',
        checked: selectedNewContacts.value.includes(phone),
        onChange: event => handleSelection(event, phone),
      });
    },
  }),
];

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  columns,
  getCoreRowModel: getCoreRowModel(),
});

const addNewContacts = async () => {
  const { data } = await currentAPI.value.userControlInGroup(
    props.currentContact.id,
    props.inbox.phone_number,
    selectedNewContacts.value,
    'add'
  );
  if ((data.result && data.result.length > 0) || data.success) {
    emit('addedNewContacts');
    selectedNewContacts.value = [];
  }
};

const onClose = () => emit('onClose');

onMounted(() => {
  const numbersOnGroup = props.currentMembers.map(c =>
    c.phone.includes('+') ? c.phone : '+' + c.phone
  );
  const ids = new Set();
  props.availableContacts.forEach(contact => {
    const { additional_attributes: at, phone_number, id } = contact;
    if (!phone_number || (at && at.isGroup)) return;
    if (numbersOnGroup.includes(phone_number)) return;
    if (ids.has(id)) return;
    contacts.value.push(contact);
    ids.add(id);
  });
});
</script>

<template>
  <woot-modal :show="show" :on-close="onClose">
    <div class="column content-box">
      <woot-modal-header header-title="Contatos disponíveis" />
      <section class="contacts-table-wrap">
        <Table :table="table" class="max-h-[calc(100vh-12rem)]" />
        <EmptyState
          v-if="!contacts.length"
          :title="$t('CONTACTS_PAGE.LIST.NO_CONTACTS')"
        />
      </section>
    </div>
    <div class="modal-footer delete-item">
      <button
        :disabled="selectedNewContacts.length === 0"
        class="button success large expanded nice"
        @click="addNewContacts"
      >
        Adicionar participante{{ selectedNewContacts.length === 1 ? '' : 's' }}
      </button>
    </div>
  </woot-modal>
</template>
